import React, { useState, useEffect, useCallback } from "react";
import SectionTitleS2 from "../SectionTitleS2";
import { useLanguage } from "../../contexts/LanguageContext";
import { galleryImages } from "../../config/galleryImages";

// Image Gallery Modal Component with Navigation
const ImageGalleryModal = ({ images, currentIndex, onClose, onNavigate }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const currentImage = images[currentIndex];

  // Keyboard navigation
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        onNavigate(currentIndex - 1);
      } else if (e.key === "ArrowRight") {
        onNavigate(currentIndex + 1);
      }
    },
    [currentIndex, onClose, onNavigate]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    // Reset image loaded state when navigating
    setImageLoaded(false);
  }, [currentIndex]);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < images.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          background: "rgba(255, 255, 255, 0.9)",
          border: "none",
          borderRadius: "50%",
          width: "50px",
          height: "50px",
          fontSize: "32px",
          lineHeight: "1",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#333",
          fontWeight: "bold",
          transition: "all 0.3s",
          zIndex: 10000,
        }}
        onMouseEnter={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 1)";
          e.target.style.transform = "scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.target.style.background = "rgba(255, 255, 255, 0.9)";
          e.target.style.transform = "scale(1)";
        }}
      >
        ×
      </button>

      {/* Previous Button */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToPrevious();
          }}
          style={{
            position: "absolute",
            left: "20px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            borderRadius: "50%",
            width: "60px",
            height: "60px",
            fontSize: "32px",
            lineHeight: "1",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#333",
            fontWeight: "bold",
            transition: "all 0.3s",
            zIndex: 10000,
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 1)";
            e.target.style.transform = "translateY(-50%) scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 0.9)";
            e.target.style.transform = "translateY(-50%) scale(1)";
          }}
        >
          ‹
        </button>
      )}

      {/* Next Button */}
      {currentIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          style={{
            position: "absolute",
            right: "20px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "rgba(255, 255, 255, 0.9)",
            border: "none",
            borderRadius: "50%",
            width: "60px",
            height: "60px",
            fontSize: "32px",
            lineHeight: "1",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#333",
            fontWeight: "bold",
            transition: "all 0.3s",
            zIndex: 10000,
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 1)";
            e.target.style.transform = "translateY(-50%) scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "rgba(255, 255, 255, 0.9)";
            e.target.style.transform = "translateY(-50%) scale(1)";
          }}
        >
          ›
        </button>
      )}

      {/* Image Container */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: "95vw",
          maxHeight: "95vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {!imageLoaded && (
          <div
            style={{
              position: "absolute",
              color: "white",
              fontSize: "18px",
            }}
          >
            Loading...
          </div>
        )}
        <img
          src={currentImage?.Pimg || currentImage?.url}
          alt={currentImage?.name || `Image ${currentIndex + 1}`}
          style={{
            maxWidth: "95vw",
            maxHeight: "95vh",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 0.3s",
          }}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            console.error("Failed to load image:", currentImage?.Pimg);
            setImageLoaded(true);
            e.target.style.opacity = 0.5;
          }}
        />

        {/* Image Counter */}
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            background: "rgba(0, 0, 0, 0.7)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "20px",
            fontSize: "14px",
            pointerEvents: "none",
          }}
        >
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

const PortfolioSectionInner = (props) => {
  const { t } = useLanguage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use local gallery images
      if (galleryImages && galleryImages.length > 0) {
        const portfolios = galleryImages.map((imgUrl) => ({
          Pimg: imgUrl,
          thumbnail: imgUrl,
        }));
        setImages(portfolios);
        setLoading(false);
        return;
      }

      // No images found
      setError(
        "No images found. Please add images to src/images/our-moments/."
      );
      setLoading(false);
    } catch (err) {
      console.error("Error loading gallery images:", err);
      setError(
        err.message ||
          "Failed to load images. Please check your local gallery configuration."
      );
      setLoading(false);
    }
  };

  // Convert images to portfolio objects
  const Portfolios = images;

  return (
    <section
      className={`wpo-portfolio-section-s2 section-padding ${props.pSclass}`}
      id="gallery"
      style={{ marginTop: "60px", marginBottom: "60px" }}
    >
      <div className="container-fluid">
        <SectionTitleS2 MainTitle={t("gallery.title")} />
        <style>
          {`
            .wpo-portfolio-section-s2 .portfolio-grids .grid .img-holder:before {
              display: none !important;
            }
            .wpo-portfolio-section-s2 .portfolio-grids .grid .img-holder {
              background: transparent !important;
              border: none !important;
              padding: 0 !important;
            }
            .wpo-portfolio-section-s2 .portfolio-grids .grid .img-holder img {
              background: transparent !important;
              border: none !important;
            }
            /* Force uniform grid layout */
            .wpo-portfolio-section-s2 .portfolio-grids {
              display: grid !important;
              grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important;
              gap: 15px !important;
              margin: 0 !important;
            }
            .wpo-portfolio-section-s2 .portfolio-grids .grid {
              width: 100% !important;
              float: none !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .wpo-portfolio-section-s2 .portfolio-grids .grid:first-child,
            .wpo-portfolio-section-s2 .portfolio-grids .grid:last-child {
              width: 100% !important;
            }
            @media (max-width: 991px) {
              .wpo-portfolio-section-s2 .portfolio-grids {
                grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)) !important;
              }
            }
            @media (max-width: 600px) {
              .wpo-portfolio-section-s2 .portfolio-grids {
                grid-template-columns: 1fr !important;
              }
            }
          `}
        </style>
        <div className="sortable-gallery">
          <div className="row">
            <div className="col-lg-12">
              <div className="portfolio-grids gallery-container clearfix">
                {loading ? (
                  <div
                    className="col-12 text-center"
                    style={{ padding: "40px", color: "#666" }}
                  >
                    <p>Loading images...</p>
                    <div style={{ marginTop: "20px" }}>
                      <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                    </div>
                  </div>
                ) : error ? (
                  <div
                    className="col-12 text-center"
                    style={{ padding: "40px", color: "#d32f2f" }}
                  >
                    <p>
                      <strong>Error loading images:</strong>
                    </p>
                    <p>{error}</p>
                  </div>
                ) : Portfolios.length > 0 ? (
                  <>
                    {Portfolios.map((portfolio, pitem) => (
                      <div className="grid" key={portfolio.id || pitem}>
                        <div
                          className="img-holder"
                          style={{
                            position: "relative",
                            width: "100%",
                            overflow: "visible", // Changed from hidden to visible
                            cursor: "pointer",
                            backgroundColor: "transparent",
                            border: "none",
                            padding: 0,
                            margin: 0,
                          }}
                          onMouseEnter={(e) => {
                            // Prevent hover overlay from showing
                            const before =
                              e.currentTarget.querySelector("::before");
                            if (before) before.style.display = "none";
                          }}
                        >
                          <img
                            src={portfolio.thumbnail || portfolio.Pimg}
                            alt={portfolio.name || `Gallery image ${pitem + 1}`}
                            loading="lazy"
                            onClick={() => setSelectedImageIndex(pitem)}
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "600px",
                              display: "block",
                              cursor: "pointer",
                              transition: "transform 0.3s ease, opacity 0.3s",
                              objectFit: "contain",
                              margin: 0,
                              padding: 0,
                              border: "none",
                              backgroundColor: "transparent",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = "scale(1.03)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = "scale(1)";
                            }}
                            onError={(e) => {
                              console.error(
                                "Image load error:",
                                portfolio.thumbnail || portfolio.Pimg
                              );
                              // Try alternative URL format if first fails
                              const currentSrc = e.target.src;
                              if (currentSrc.includes("uc?export=view")) {
                                // If uc?export=view failed, try with different format
                                const fileIdMatch =
                                  currentSrc.match(/id=([^&]+)/);
                                if (fileIdMatch) {
                                  const fileId = fileIdMatch[1];
                                  // Try thumbnail format with larger size for better quality
                                  e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1200`;
                                }
                              } else {
                                // Hide image if all attempts fail
                                e.target.style.display = "none";
                              }
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    {/* Image Gallery Lightbox with Navigation */}
                    {selectedImageIndex !== null && (
                      <ImageGalleryModal
                        images={Portfolios}
                        currentIndex={selectedImageIndex}
                        onClose={() => setSelectedImageIndex(null)}
                        onNavigate={(newIndex) => {
                          if (newIndex >= 0 && newIndex < Portfolios.length) {
                            setSelectedImageIndex(newIndex);
                          }
                        }}
                      />
                    )}
                  </>
                ) : (
                  <div
                    className="col-12 text-center"
                    style={{ padding: "40px", color: "#666" }}
                  >
                    <p>No images found in the Google Drive folder.</p>
                    <p
                      style={{
                        fontSize: "14px",
                        marginTop: "10px",
                        fontStyle: "italic",
                      }}
                    >
                      Make sure your folder is shared and contains image files.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PortfolioSection = (props) => {
  return <PortfolioSectionInner {...props} />;
};

export default PortfolioSection;
